import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, CircleCheckBig, Hash, KeyRound, Loader2, LockKeyhole } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate } from 'react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { apiErrorMessage } from '@/i18n'
import { authApi } from '@/lib/auth/api'
import { useAuth } from '@/lib/auth/AuthProvider'
import { AuthHeading, FieldError, FormAlert, IconInput } from './components/AuthFields'
import { AuthShell } from './components/AuthShell'

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const { status } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const schema = z
    .object({
      pre_auth_token: z.string().min(1, t('auth.resetTokenRequired')),
      otp: z.string().regex(/^\d{6}$/, t('auth.otpInvalid')),
      new_password: z.string().min(8, t('auth.changePassword.tooShort')),
      confirm: z.string().min(1),
    })
    .refine((v) => v.new_password === v.confirm, { message: t('auth.changePassword.mismatch'), path: ['confirm'] })

  const form = useForm({ resolver: zodResolver(schema) })
  const errors = form.formState.errors

  if (status === 'authenticated') return <Navigate to="/" replace />

  if (done) {
    return (
      <AuthShell>
        <div className="space-y-5">
          <AuthHeading
            eyebrow={
              <span className="flex size-12 items-center justify-center rounded-2xl bg-success/12 text-success ring-1 ring-inset ring-success/20">
                <CircleCheckBig className="size-6" aria-hidden />
              </span>
            }
            title={t('auth.resetDoneTitle')}
            subtitle={t('auth.resetDoneBody')}
          />
          <Button asChild className="h-11 w-full rounded-xl">
            <Link to="/login">{t('auth.title')}</Link>
          </Button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit(async (v) => {
          setError(null)
          try {
            await authApi.resetPassword(v.pre_auth_token.trim(), v.otp, v.new_password)
            setDone(true)
          } catch (e) {
            setError(apiErrorMessage(e))
          }
        })}
      >
        <AuthHeading title={t('auth.resetTitle')} subtitle={t('auth.resetSubtitle')} />
        <div className="space-y-2">
          <Label htmlFor="token">{t('auth.resetToken')}</Label>
          <IconInput id="token" icon={KeyRound} autoComplete="off" {...form.register('pre_auth_token')} />
          <FieldError message={errors.pre_auth_token?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="otp">{t('auth.otp')}</Label>
          <IconInput id="otp" icon={Hash} inputMode="numeric" maxLength={6} autoComplete="one-time-code" className="font-mono tracking-[0.3em]" {...form.register('otp')} />
          <FieldError message={errors.otp?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="next">{t('auth.changePassword.next')}</Label>
          <IconInput id="next" icon={LockKeyhole} type="password" revealable autoComplete="new-password" {...form.register('new_password')} />
          <FieldError message={errors.new_password?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">{t('auth.changePassword.confirm')}</Label>
          <IconInput id="confirm" icon={LockKeyhole} type="password" revealable autoComplete="new-password" {...form.register('confirm')} />
          <FieldError message={errors.confirm?.message} />
        </div>
        {error ? <FormAlert>{error}</FormAlert> : null}
        <Button type="submit" className="h-11 w-full rounded-xl" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? <Loader2 className="animate-spin" /> : <LockKeyhole />}
          {t('auth.resetSubmit')}
        </Button>
        <Button asChild type="button" variant="ghost" className="w-full">
          <Link to="/login">
            <ArrowLeft />
            {t('auth.useOther')}
          </Link>
        </Button>
      </form>
    </AuthShell>
  )
}
